// The "Invite by email" write on `/admin` Users — the state half of
// `<AdminInviteByEmail/>`, split like `lib/access-request-actions.ts`: a dialog, one
// write, its result and its refusal are STATE, and they unit-test without a shell.
//
//   - The write is `provisionUser` (`lib/admin-provision.ts`) on an `admin:grants`
//     operator credential. A door refusal sends nothing, lands in `refusal()` for
//     `<OperatorRefusalAlert/>`, and closes the dialog (nothing in the form cures it).
//   - A refused write renders the API's own sentence and the dialog STAYS OPEN.
//   - The accept link is held in `result()` until the dialog closes, and closing
//     DROPS it: the provision answer is the only place it ever appears.
//   - A landed write re-reads the registry through `onInvited`, never a patch.

import { createSignal } from 'solid-js';
import { t } from '../i18n';
import { type ProvisionAnswer, type ProvisionInput, provisionUser } from './admin-provision';
import { createMutation } from './mutation';
import type { OperatorRefusal } from './operator-credential';

export type InviteByEmail = {
  readonly isOpen: () => boolean;
  readonly open: () => void;
  /** Close the dialog; drops a held accept link. */
  readonly close: () => void;
  readonly busy: () => boolean;
  readonly error: () => string | null;
  readonly refusal: () => OperatorRefusal | null;
  readonly result: () => ProvisionAnswer | null;
  readonly send: (input: ProvisionInput) => void;
};

export function createInviteByEmail(token: string | null, onInvited: () => void): InviteByEmail {
  const [isOpen, setOpen] = createSignal(false);
  const [result, setResult] = createSignal<ProvisionAnswer | null>(null);
  const [refusal, setRefusal] = createSignal<OperatorRefusal | null>(null);

  const write = createMutation({
    call: (input: ProvisionInput) => provisionUser(input, token),
    fallback: t('admin.invite_email_failed'),
    onSuccess: (out) => {
      if (out.kind === 'ok') {
        setResult(out.value);
        onInvited();
        return;
      }
      setRefusal(out.kind);
      setOpen(false);
    },
  });

  return {
    isOpen,
    open: () => {
      write.clearError();
      setRefusal(null);
      setResult(null);
      setOpen(true);
    },
    close: () => {
      setOpen(false);
      setResult(null);
    },
    busy: write.pending,
    error: write.error,
    refusal,
    result,
    send: (input) => {
      if (!write.pending()) void write.run(input);
    },
  };
}
